import React, { useState } from 'react';
import { Box, Flex, Center, VStack, Text, Spinner, Button, Icon } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { AiOutlineReload, AiOutlineWarning } from 'react-icons/ai';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import { useNogleTech } from '../../contexts/NogleTechContext';

const pulseGlow = keyframes`
    0%, 100% { opacity: 0.6; filter: drop-shadow(0 0 8px rgba(1, 233, 117, 0.3)); }
    50% { opacity: 1; filter: drop-shadow(0 0 20px rgba(1, 233, 117, 0.6)); }
`;

const dotBounce = keyframes`
    0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
`;

const logoUrl =
    'https://s3-contel-imagens-aplicacao.s3.us-east-1.amazonaws.com/nogle/Brand.svg';

/* ─── Tela de Loading Fullscreen ─── */
const LoadingScreen = ({ statusMsg }) => (
    <Center
        position="fixed"
        top={0}
        left={0}
        w="100vw"
        h="100vh"
        bg="background"
        zIndex={9999}
    >
        <VStack spacing={8}>
            {/* Logo animado */}
            <Box
                as="img"
                src={logoUrl}
                alt="Nogle"
                w="120px"
                animation={`${pulseGlow} 2s ease-in-out infinite`}
            />

            {/* Spinner + dots */}
            <VStack spacing={4}>
                <Spinner
                    size="lg"
                    color="secondary"
                    thickness="3px"
                    speed="0.8s"
                    emptyColor="borderPrimary"
                />
                <Flex gap="6px" align="center">
                    {[0, 1, 2].map((i) => (
                        <Box
                            key={i}
                            w="6px"
                            h="6px"
                            borderRadius="full"
                            bg="secondary"
                            animation={`${dotBounce} 1.4s ease-in-out ${i * 0.16}s infinite`}
                        />
                    ))}
                </Flex>
            </VStack>

            {/* Status text */}
            <VStack spacing={1}>
                <Text
                    color="textSecondary"
                    fontSize="sm"
                    fontWeight="600"
                    letterSpacing="0.5px"
                >
                    {statusMsg || 'Conectando ao banco de dados...'}
                </Text>
                <Text color="textMuted" fontSize="xs">
                    NOGLE TECH — Painel Comercial
                </Text>
            </VStack>
        </VStack>
    </Center>
);

/* ─── Tela de Erro com Retry ─── */
const ErrorScreen = ({ erro, onRetry }) => (
    <Center
        position="fixed"
        top={0}
        left={0}
        w="100vw"
        h="100vh"
        bg="background"
        zIndex={9999}
    >
        <VStack spacing={8} maxW="420px" textAlign="center" px={6}>
            {/* Logo */}
            <Box as="img" src={logoUrl} alt="Nogle" w="100px" opacity={0.5} />

            {/* Icon de erro */}
            <Flex
                w="64px"
                h="64px"
                borderRadius="full"
                bg="rgba(252, 129, 129, 0.12)"
                border="1px solid rgba(252, 129, 129, 0.25)"
                align="center"
                justify="center"
            >
                <Icon as={AiOutlineWarning} color="danger" boxSize="28px" />
            </Flex>

            {/* Mensagem */}
            <VStack spacing={2}>
                <Text color="textPrimary" fontSize="lg" fontWeight="800">
                    Falha na Conexão
                </Text>
                <Text color="textSecondary" fontSize="sm" lineHeight="1.6">
                    {erro || 'Não foi possível conectar ao servidor.'}
                </Text>
                <Text color="textMuted" fontSize="xs">
                    Verifique se o backend (Cartman) está rodando em localhost:3333
                </Text>
            </VStack>

            {/* Botão Retry */}
            <Button
                leftIcon={<AiOutlineReload />}
                bg="secondary"
                color="primary"
                size="lg"
                borderRadius="xl"
                fontWeight="800"
                px={8}
                _hover={{ bg: 'secondaryHover', transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(1,233,117,0.3)' }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.2s"
                onClick={onRetry}
            >
                Tentar Novamente
            </Button>

            <Text color="textMuted" fontSize="10px" letterSpacing="1px" textTransform="uppercase">
                NOGLE TECH v3.0
            </Text>
        </VStack>
    </Center>
);

const MainLayout = ({ children }) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const { loading, erro, statusMsg, refetchMarcas } = useNogleTech();

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    // Mostra loading screen enquanto carrega marcas
    if (loading) {
        return <LoadingScreen statusMsg={statusMsg} />;
    }

    // Mostra tela de erro se todas as tentativas falharam
    if (erro) {
        return <ErrorScreen erro={erro} onRetry={refetchMarcas} />;
    }

    return (
        <Flex h="100vh" bg="background" overflow="hidden">
            <Sidebar
                isCollapsed={isSidebarCollapsed}
                onToggle={toggleSidebar}
            />
            <Flex
                direction="column"
                flex="1"
                minW={0}
                w="full"
                h="100vh"
            >
                <Header />
                <Box flex="1" overflow="auto" overflowX="hidden" minW={0} p={8}>
                    {children}
                </Box>
                <Footer />
            </Flex>
        </Flex>
    );
};

export default MainLayout;